import * as React from "react";
import Icon, { PACKS } from "common/icons";
import PACK from "common/icons/packs";

// actions
import {
  clearMedia as clearMediaAction,
  uploadMedia as uploadMediaAction,
} from "@vingle/wtf-sdk/actions/mediaUpload";
import { pushToast as pushToastAction } from "@vingle/wtf-sdk/actions/toast";

// components
import PulseLoadingDot from "common/components/pulseLoadingDot";
import MediaUploadInput from "app/main/mediaUpload/input";
import {
  ThreadInputContainer,
  ThreadInputMedia,
  ThreadInputContentWrapper,
  ThreadInputContent,
  ThreadInputSubmit,
  UploadedMediaWrapper,
  UploadedImage,
  UploadedMediaPreview,
  DeleteMediaButton,
  MediaLoadingDotWrapper,
  UploadedLink,
  LinkPreview,
  ThreadMediaArea,
} from "./styledComponents";

// helpers
import { v4APIErrorCodeParser } from "@vingle/wtf-sdk/helpers/v4APIErrorParser";
import { isBrowser } from "@vingle/wtf-sdk/helpers/envChecker";
import MediaAPI from "@vingle/wtf-sdk/api/media";
import { useCancelToken, useEventCallback } from "@vingle/wtf-sdk/helpers/hook";
import { useStoreState, useActions } from "app/main/store";
import { useIntl } from "react-intl";
import fastDeepEqual from "fast-deep-equal";
import ResponsiveTextArea from "common/components/responsiveTextArea";

const MEDIA_UPLOAD_INPUT_DENY_TYPES = ["video" as const];

export enum POSITION_TYPE {
  STICKY,
  FIXED_BOTTOM,
  STATIC,
}

// hooks
interface IProps {
  id: Vingle.Id | string;
  onInteraction?: () => boolean;
  onAddThread: (
    content: string,
    media?: Vingle.MediaUpload.IUploadInput,
    linkData?: Vingle.LinkUploadData | null,
  ) => void;
  placeholder?: string;
  backgroundColor?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  inputId?: string;
  disableUpload?: boolean;
  disableLink?: boolean;
  targetUser?: Vingle.User.ISimplifiedUser | null;
  positionType?: POSITION_TYPE;
}

function ThreadInput(props: IProps) {
  const {
    backgroundColor,
    placeholder,
    autoFocus,
    inputId,
    disableLink,
    disableUpload,
    targetUser,
    positionType,
    disabled,
  } = props;
  const hookProps = useThreadInputProps(props);
  const {
    media,
    linkData,
    isLinkDataLoading,
    content,
    isDisabled,
    mediaUploadInput,
    setContent,
    textareaRef,
  } = hookProps;
  const {
    handleSubmit,
    handleInteraction,
    handleImageInputClick,
    handleLinkClick,
    handleInput,
    handleMediaChange,
    handleDeleteMedia,
  } = useThreadInputHandlers(props, hookProps);
  React.useEffect(() => {
    setContent(targetUser ? `@${targetUser.username} ` : "");
    if (targetUser && textareaRef.current) {
      textareaRef.current.blur();
      textareaRef.current.focus();
    }
  }, [targetUser]);
  return (
    <ThreadInputContainer
      positionType={positionType}
      onSubmit={handleSubmit}
      onClickCapture={handleInteraction}
    >
      <ThreadMediaArea>
        {!disableUpload ? (
          <ThreadInputMedia
            onClick={handleImageInputClick}
            disabled={Boolean(linkData)}
          >
            <Icon icon={PACK.COMMENT_INPUT.ALBUM} width={24} height={24} />
          </ThreadInputMedia>
        ) : null}
        {!disableLink ? (
          <ThreadInputMedia onClick={handleLinkClick} disabled={Boolean(media)}>
            <Icon icon={PACK.COMMENT_INPUT.LINK} width={24} height={24} />
          </ThreadInputMedia>
        ) : null}
      </ThreadMediaArea>
      <ThreadInputContentWrapper style={{ backgroundColor }}>
        <ThreadInputUploadArea
          linkData={linkData}
          isLinkDataLoading={isLinkDataLoading}
          media={media}
          handleDeleteMedia={handleDeleteMedia}
        />
        <ThreadInputContent>
          <ResponsiveTextArea
            ref={textareaRef}
            className={"ThreadInput"}
            content={content}
            placeholder={placeholder}
            onContentChange={handleInput}
            autoFocus={autoFocus}
            id={inputId}
            disabled={disabled}
          />
          <ThreadInputSubmit disabled={isDisabled}>
            <Icon icon={PACK.COMMENT_INPUT.SUBMIT} width={24} height={24} />
          </ThreadInputSubmit>
        </ThreadInputContent>
      </ThreadInputContentWrapper>
      <MediaUploadInput
        onChange={handleMediaChange}
        ref={mediaUploadInput}
        denyTypes={MEDIA_UPLOAD_INPUT_DENY_TYPES}
      />
    </ThreadInputContainer>
  );
}

interface IHookProps extends ReturnType<typeof useThreadInputProps> {}

function useThreadInputProps(props: IProps) {
  const states = useStoreState(
    state => ({
      currentUser: state.app.currentUser,
      mediaInputs: state.mediaUpload.input,
    }),
    (curr, prev) => fastDeepEqual(curr, prev),
  );
  const media = states.mediaInputs.find(
    input => input.id === String(props.id),
  ) as Vingle.MediaUpload.IUploadInput<Vingle.MediaUpload.MediaUploadData>;
  const actions = useActions({
    uploadMedia: uploadMediaAction,
    clearMedia: clearMediaAction,
    pushToast: pushToastAction,
  });
  const [content, setContent] = React.useState("");
  const [linkData, setLinkData] = React.useState<Vingle.LinkUploadData | null>(
    null,
  );
  const [isLinkDataLoading, setLinkDataLoading] = React.useState(false);
  const cancelToken = useCancelToken();
  const intl = useIntl();
  const mediaUploadInput: React.RefObject<MediaUploadInput> = React.useRef(
    null,
  );
  const textareaRef: React.MutableRefObject<ResponsiveTextArea | null> = React.useRef<
    ResponsiveTextArea
  >(null);
  const isDisabled =
    !(content.trim() || media || linkData) ||
    (media && media.meta.isLoading) ||
    isLinkDataLoading;
  return {
    ...states,
    ...actions,
    media,
    content,
    setContent,
    linkData,
    setLinkData,
    isLinkDataLoading,
    setLinkDataLoading,
    cancelToken,
    intl,
    mediaUploadInput,
    textareaRef,
    isDisabled,
  };
}

// tslint:disable-next-line:cognitive-complexity
function useThreadInputHandlers(props: IProps, hookProps: IHookProps) {
  const { autoFocus, id, onInteraction } = props;
  const {
    textareaRef,
    intl,
    currentUser,
    setLinkDataLoading,
    isDisabled,
    content,
    media,
    linkData,
    setContent,
    mediaUploadInput,
    clearMedia,
    setLinkData,
    uploadMedia,
    pushToast,
    cancelToken,
  } = hookProps;
  const handleAutoFocus: React.FormEventHandler = React.useCallback(() => {
    if (isBrowser() && autoFocus && onInteraction && onInteraction()) {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 1000);
    }
  }, [autoFocus]);
  const handleSubmit: React.FormEventHandler = useEventCallback(
    e => {
      e.preventDefault();
      if (isDisabled) {
        return;
      }
      props.onAddThread(content, media, linkData);
      if (textareaRef.current) {
        setContent("");
        textareaRef.current.setValue("");
      }
      handleDeleteMedia();
    },
    [media, content, isDisabled],
  );
  const handleDeleteMedia = React.useCallback(() => {
    if (mediaUploadInput.current) {
      mediaUploadInput.current.clear();
    }
    clearMedia({ id: id.toString() });
    setLinkData(null);
  }, [id]);
  const handleMediaChange = React.useCallback(
    (file: File | Blob) => {
      uploadMedia({
        file,
        id: id.toString(),
        cancelToken: cancelToken.current.token,
      });
    },
    [id],
  );
  const handleInteraction = useEventCallback(
    (e: React.SyntheticEvent) => {
      if (onInteraction && !onInteraction() && textareaRef.current) {
        e.stopPropagation();
        textareaRef.current.blur();
      }
    },
    [onInteraction],
  );
  const handleImageInputClick: React.MouseEventHandler = useEventCallback(
    e => {
      e.preventDefault();
      if (currentUser && mediaUploadInput.current) {
        if (mediaUploadInput.current.hasFile()) {
          pushToast({
            type: "INFO",
            message: intl.formatMessage({
              id: "thread_input_only_single_attachment",
            }),
          });
        }

        mediaUploadInput.current.trigger();
      }
    },
    [currentUser],
  );
  const handleLinkClick: React.MouseEventHandler = useEventCallback(
    async e => {
      e.preventDefault();
      if (currentUser) {
        let link = prompt(
          intl.formatMessage({
            id: "thread_input_insert_link_placeholder",
          }),
        );
        if (link) {
          if (!link.startsWith("http://") && !link.startsWith("https://")) {
            link = `http://${link}`;
          }
          setLinkDataLoading(true);
          try {
            setLinkData(
              await MediaAPI.uploadLink(link, cancelToken.current.token),
            );
          } catch (err) {
            const errorCode = v4APIErrorCodeParser(err);
            if (errorCode === "LINK_NOT_FOUND") {
              pushToast({
                type: "DANGER",
                message: intl.formatMessage({
                  id: "thread_input_insert_link_error_linkpreview_load",
                }),
              });
            } else {
              pushToast({
                type: "DANGER",
                message: intl.formatMessage({
                  id: "thread_input_insert_link_error_wrong_url",
                }),
              });
            }
            setLinkData(null);
          }
          setLinkDataLoading(false);
        }
      }
    },
    [currentUser],
  );
  const handleInput = useEventCallback((newContent: string) => {
    setContent(newContent);
  });
  return {
    handleLinkClick,
    handleAutoFocus,
    handleMediaChange,
    handleInput,
    handleImageInputClick,
    handleSubmit,
    handleDeleteMedia,
    handleInteraction,
  };
}

const ThreadInputUploadArea = React.memo<{
  media?: Vingle.MediaUpload.IUploadInput;
  isLinkDataLoading: boolean;
  linkData: Vingle.LinkUploadData | null;
  handleDeleteMedia: () => void;
}>(props => {
  const { media, linkData, isLinkDataLoading, handleDeleteMedia } = props;
  const mediaLoading = Boolean(media && media.meta.isLoading);
  if (!media && !mediaLoading && !linkData && !isLinkDataLoading) {
    return null;
  }
  let uploadedMedia: React.ReactNode = null;
  if (media) {
    uploadedMedia = (
      <UploadedImage>
        {media.meta.animated ? (
          <Icon
            icon={PACKS.MEDIA.GIF}
            width={33}
            height={22}
            className="gifIcon"
          />
        ) : null}
        <UploadedMediaPreview src={media.meta.thumbnail} />
      </UploadedImage>
    );
  }
  if (linkData) {
    switch (linkData.type) {
      case "og-object":
        uploadedMedia = (
          <UploadedLink>
            <LinkPreview linkPreview={linkData.data} />
          </UploadedLink>
        );

        break;
      case "embedded-video":
        uploadedMedia = (
          <UploadedImage>
            <Icon
              icon={PACKS.MEDIA.VIDEO_PLAY_BUTTON}
              width={24}
              height={24}
              className="videoPlayButton"
            />
            <UploadedMediaPreview src={linkData.data.poster} />
          </UploadedImage>
        );
        break;
    }
  }
  return (
    <UploadedMediaWrapper
      style={{
        display:
          isLinkDataLoading || (linkData && linkData.type === "og-object")
            ? "block"
            : "inline-block",
      }}
    >
      {mediaLoading || isLinkDataLoading ? (
        <MediaLoadingDotWrapper>
          <PulseLoadingDot color={isLinkDataLoading ? "red" : "gray"} />
        </MediaLoadingDotWrapper>
      ) : null}
      {uploadedMedia}
      <DeleteMediaButton onClick={handleDeleteMedia}>
        <Icon icon={PACKS.COMMENT.DELETE_RESOURCE} width={18} height={18} />
      </DeleteMediaButton>
    </UploadedMediaWrapper>
  );
});

export default React.memo(ThreadInput);
